import React, { useEffect, useState } from "react";
import ProfesionalCard from "./ProfesionalCard";
import ProjectCard from "./ProjectCard";
import {
  Pencil, X, Save, Camera, Mail, Ticket, Linkedin, ExternalLink,
  Building2, Rocket, Plus, UserRound, Briefcase, AlertCircle, ImagePlus, Tags
} from "lucide-react";
import Avatar from "./Avatar";
import CategoryPicker from "./CategoryPicker";
import { Loader, trackPointer } from "./UI";
import {
  generarInvitacion, DEFAULT_BIO, BIO_MAX_LENGTH, getValidUrl, fileToBase64, MAX_IMAGE_MB
} from "../utils/Utils";



/** Tarjeta de empresa o proyecto, con estado de "pendiente". */
function ItemCard({ accepted, onClick, clickable, children }) {
  return (
    <div
      className={`dyd-item-card ${accepted ? "" : "is-pending"} ${clickable ? "is-clickable" : ""}`}
      onClick={onClick}
      onMouseMove={trackPointer}
    >
      {children}
      {!accepted && (
        <span className="dyd-pending">
          <AlertCircle size={13} /> Pendiente de aceptación
        </span>
      )}
    </div>
  );
}

export default function ProfileComponent({ profile, companies, projects, loading }) {
  const [isEditing, setIsEditing] = useState(false);
  const [showCompanyPopup, setShowCompanyPopup] = useState(false);
  const [showProjectPopup, setShowProjectPopup] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState("personal"); // "personal" o "profesional"

  const [formData, setFormData] = useState({
    nombre: profile?.name || "",
    apellidos: profile?.surname || "",
    profilePicture: profile?.profilePicture || "",
    coverPicture: profile?.coverPicture || "",
    bio: profile?.bio || "",
    linkedin: profile?.linkedin || "",
    networkingCategories: profile?.networkingCategories || [],
  });

  // El perfil llega del servidor despues del primer render: sin esto el
  // formulario se queda con los valores vacios del estado inicial y al guardar
  // mandaria campos en blanco
  useEffect(() => {
    if (!profile) return;
    setFormData({
      nombre: profile.name || "",
      apellidos: profile.surname || "",
      profilePicture: profile.profilePicture || "",
      coverPicture: profile.coverPicture || "",
      bio: profile.bio || "",
      linkedin: profile.linkedin || "",
      networkingCategories: profile.networkingCategories || [],
    });
  }, [profile]);

  const handleActiveTabChange = (tab) => {
    setIsEditing(false);
    setActiveTab(tab);
  };

  /** Lee el archivo elegido y lo guarda en `field` (avatar o portada). */
  const handleImageChange = (field) => async (e) => {
    const file = e.target.files[0];
    // Se limpia el input para que elegir el mismo archivo dos veces dispare el change
    e.target.value = "";
    if (!file) return;

    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      alert(`La imagen no puede pesar más de ${MAX_IMAGE_MB} MB.`);
      return;
    }

    // Viaja en base64 dentro del JSON; es el backend quien la sube a imgbb
    try {
      setFormData({ ...formData, [field]: await fileToBase64(file) });
    } catch (error) {
      console.error("Error al leer la imagen:", error);
      alert("No se ha podido leer la imagen. Prueba con otro archivo.");
    }
  };

  const fieldMap = { nombre: "name", apellidos: "surname" };

  const toggleEdit = () => setIsEditing(!isEditing);

  const handleChange = (field) => (e) => {
    const value = field === "meetingAcceptance" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
  };


  const handleSaveChanges = async () => {
    console.log("Guardando cambios...", formData);

    const token = localStorage.getItem("token");
    if (!token) {
      alert("No estás autenticado. Por favor, inicia sesión.");
      window.location.href = "/";
      return;
    }

    const userId = localStorage.getItem("userId");
    //Todo lo que va vacio no lo tenemos en cuenta
    const updatedData = {};
    if (formData.nombre && formData.nombre !== profile.name) updatedData.name = formData.nombre;
    if (formData.apellidos && formData.apellidos !== profile.surname) updatedData.surname = formData.apellidos;
    if (formData.profilePicture && formData.profilePicture !== profile.profilePicture) {
      updatedData.profilePicture = formData.profilePicture;
    }
    // La portada sí se manda vacía: así se puede quitar y volver al degradado
    if (formData.coverPicture !== (profile.coverPicture || "")) {
      updatedData.coverPicture = formData.coverPicture;
    }
    if (formData.linkedin && formData.linkedin !== profile.linkedin) updatedData.linkedin = formData.linkedin;
    if (formData.bio && formData.bio !== profile.bio) updatedData.bio = formData.bio;
    // Las categorías van como lista completa: también cuando se queda vacía,
    // para poder quitar las que ya no encajan
    const savedCategories = profile.networkingCategories || [];
    if (JSON.stringify(formData.networkingCategories) !== JSON.stringify(savedCategories)) {
      updatedData.networkingCategories = formData.networkingCategories;
    }

    if (Object.keys(updatedData).length === 0) {
      alert("No hay cambios para guardar.");
      return;
    }
    console.log("Datos a enviar:", updatedData);
    console.log("Del usuario:", userId);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData),
      });
      const data = await response.json();
      if (response.ok) {
        // La ruta responde { message, user }
        const saved = data.user || data;
        // Actualizar el estado del perfil con los nuevos datos
        setFormData({
          nombre: saved.name,
          apellidos: saved.surname,
          profilePicture: saved.profilePicture,
          coverPicture: saved.coverPicture || "",
          bio: saved.bio,
          linkedin: saved.linkedin || "",
          networkingCategories: saved.networkingCategories || [],
        });
        // El navbar lee la identidad de localStorage: si no lo refrescamos,
        // sigue mostrando el nombre y la foto viejos hasta volver a entrar.
        if (saved.name) localStorage.setItem("profileName", saved.name);
        if (saved.surname) localStorage.setItem("profileSurname", saved.surname);
        localStorage.setItem("profileImage", saved.profilePicture || "");
        localStorage.setItem("name", saved.name || "");
      } else {
        alert("Error al guardar los cambios: " + data.message);
      }
    } catch (error) {
      console.error("Error al guardar los cambios:", error);
      alert("Error al guardar los cambios. Por favor, inténtalo de nuevo más tarde.");
    }

    window.location.href = "/profile";
  };

  const handleGenerateInvitation = async () => {
    const invitationCode = Math.random().toString(36).substring(2, 10).toUpperCase();
    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.inset = "0";
    overlay.style.backgroundColor = "rgba(4,6,10,0.78)";
    overlay.style.backdropFilter = "blur(7px)";
    overlay.style.display = "flex";
    overlay.style.justifyContent = "center";
    overlay.style.alignItems = "center";
    // Con hueco a los lados: en un móvil estrecho, sin él la ventana se sale
    overlay.style.padding = "16px";
    overlay.style.zIndex = "9999";

    const popup = document.createElement("div");
    popup.style.background = "linear-gradient(180deg, #131926, #0c1017)";
    popup.style.padding = "28px";
    popup.style.borderRadius = "20px";
    popup.style.border = "1px solid rgba(255,255,255,0.18)";
    popup.style.boxShadow = "0 28px 70px rgba(0,0,0,0.6)";
    popup.style.color = "#e9edf5";
    popup.style.width = "100%";
    popup.style.maxWidth = "360px";
    popup.style.textAlign = "center";
    popup.style.animation = "dyd-pop 0.35s cubic-bezier(0.22,1,0.36,1)";

    const title = document.createElement("h3");
    title.innerText = "Tu código de invitación";
    title.style.marginBottom = "18px";
    title.style.fontFamily = "'Sora', sans-serif";
    title.style.letterSpacing = "-0.02em";
    popup.appendChild(title);

    const input = document.createElement("input");
    input.type = "text";
    input.value = invitationCode;
    input.readOnly = true;
    input.style.width = "100%";
    input.style.boxSizing = "border-box";
    input.style.padding = "13px";
    input.style.fontSize = "19px";
    input.style.fontWeight = "700";
    input.style.letterSpacing = "0.22em";
    input.style.borderRadius = "10px";
    input.style.border = "1px solid rgba(59,130,246,0.4)";
    input.style.background = "rgba(59,130,246,0.09)";
    input.style.color = "#fff";
    input.style.textAlign = "center";
    input.style.outline = "none";

    const copyBtn = document.createElement("button");
    copyBtn.innerText = "Copiar";
    copyBtn.style.marginTop = "18px";
    copyBtn.style.padding = "11px 26px";
    copyBtn.style.borderRadius = "999px";
    copyBtn.style.border = "none";
    copyBtn.style.background = "linear-gradient(135deg, #3b82f6, #1d4ed8)";
    copyBtn.style.color = "#fff";
    copyBtn.style.fontWeight = "600";
    copyBtn.style.cursor = "pointer";
    copyBtn.style.boxShadow = "0 8px 24px rgba(59,130,246,0.32)";

    copyBtn.onclick = () => {
      input.select();
      document.execCommand("copy");
      copyBtn.innerText = "¡Copiado!";
      setTimeout(() => {
        copyBtn.innerText = "Copiar";
        document.body.removeChild(overlay);
      }, 1000);
      window.location.reload();
    };

    let userId = localStorage.getItem("userId");
    //Llamamos a updateuser para restar una invitacion
    //Ojo, si no tiene invitaciones no dejamos generar
    if (!profile.invitationsLeft || profile.invitationsLeft <= 0) {
      alert("No tienes invitaciones disponibles.");
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ $inc: { invitationsLeft: -1 } }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert("Error al guardar los cambios: " + data.message);
      }

      await generarInvitacion(invitationCode);
    } catch (error) {
      console.error("Error al guardar los cambios:", error);
      alert("Error al guardar los cambios. Por favor, inténtalo de nuevo más tarde.");
    }

    popup.appendChild(input);
    popup.appendChild(copyBtn);
    overlay.appendChild(popup);
    document.body.appendChild(overlay);
    input.focus();
  };

  const editCompany = (company) => {
    setSelectedCompany(company);
    setShowCompanyPopup(true);
  };

  const editProject = (project) => {
    setSelectedProject(project || null);
    setShowProjectPopup(true);
  };

  if (loading) return <Loader text="Cargando perfil" />;

  if (!profile) {
    return (
      <div className="dyd-page" style={{ display: "grid", placeItems: "center" }}>
        <div className="dyd-empty" style={{ maxWidth: 460 }}>
          <AlertCircle size={42} strokeWidth={1.3} />
          <h3>No hemos podido cargar tu perfil</h3>
          <p>Por favor, inicia sesión nuevamente.</p>
        </div>
      </div>
    );
  }

  const picture = formData.profilePicture || profile.profilePicture;
  const cover = isEditing ? formData.coverPicture : (profile.coverPicture || "");

  return (
    <div className="dyd-page">
      <div className="dyd-profile-inner">

        {/* ─── Cabecera con portada ─── */}
        <header className="dyd-profile-hero dyd-rise">
          <div
            className={`dyd-profile-cover ${cover ? "has-image" : ""}`}
            style={cover ? { backgroundImage: `url(${cover})` } : undefined}
          >
            {isEditing && (
              <div className="dyd-cover-edit">
                <label
                  htmlFor="coverPictureInput"
                  className="dyd-btn dyd-btn-ghost dyd-btn-sm"
                  title="También se verá en tu tarjeta de la pestaña Networking"
                >
                  <ImagePlus size={15} /> {cover ? "Cambiar portada" : "Subir portada"}
                </label>
                <input
                  type="file"
                  id="coverPictureInput"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageChange("coverPicture")}
                />
                {cover && (
                  <button
                    type="button"
                    className="dyd-btn dyd-btn-subtle dyd-btn-sm"
                    onClick={() => setFormData({ ...formData, coverPicture: "" })}
                  >
                    <X size={15} /> Quitar
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="dyd-profile-identity">
            <div className="dyd-profile-avatar">
              <Avatar
                src={picture}
                name={profile.name}
                surname={profile.surname}
                size={128}
                ring
              />
              {isEditing && (
                <>
                  <label htmlFor="profilePictureInput" className="dyd-avatar-edit" title="Cambiar foto">
                    <Camera size={16} />
                  </label>
                  <input
                    type="file"
                    id="profilePictureInput"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleImageChange("profilePicture")}
                  />
                </>
              )}
            </div>

            <div className="dyd-profile-name">
              <h1>{profile.name} {profile.surname}</h1>
              <span className="dyd-meta"><Mail size={14} /> {profile.email}</span>
            </div>

            <button
              onClick={toggleEdit}
              className={`dyd-btn ${isEditing ? "dyd-btn-danger" : "dyd-btn-primary"}`}
            >
              {isEditing ? <X size={16} /> : <Pencil size={16} />}
              {isEditing ? "Cancelar" : "Editar perfil"}
            </button>
          </div>
        </header>

        {/* ─── Pestañas ─── */}
        <nav className="dyd-tabs dyd-rise" style={{ "--i": 1 }}>
          <button
            onClick={() => handleActiveTabChange("personal")}
            className={`dyd-tab ${activeTab === "personal" ? "active" : ""}`}
          >
            <UserRound size={16} /> Datos personales
          </button>
          <button
            onClick={() => handleActiveTabChange("profesional")}
            className={`dyd-tab ${activeTab === "profesional" ? "active" : ""}`}
          >
            <Briefcase size={16} /> Datos profesionales
          </button>
        </nav>

        {/* ─── Datos personales ─── */}
        {activeTab === "personal" && (
          <div className="dyd-rise" style={{ "--i": 2, display: "flex", flexDirection: "column", gap: 20 }}>

            <div className="dyd-info-grid">
              {["nombre", "apellidos"].map((field) => (
                <div key={field} className="dyd-panel dyd-info">
                  <span className="dyd-label">{field === "nombre" ? "Nombre" : "Apellidos"}</span>
                  {isEditing ? (
                    <input
                      type="text"
                      className="dyd-input"
                      value={formData[field]}
                      onChange={handleChange(field)}
                      placeholder={profile[fieldMap[field]]}
                    />
                  ) : (
                    <span className="dyd-value">{profile[fieldMap[field]]}</span>
                  )}
                </div>
              ))}
            </div>

            <div className="dyd-info-grid">
              <div className="dyd-panel dyd-info">
                <span className="dyd-label"><Mail size={12} /> Email</span>
                <span className="dyd-value">{profile.email}</span>
              </div>

              <div className="dyd-panel dyd-info dyd-invites">
                <div>
                  <span className="dyd-label"><Ticket size={12} /> Invitaciones restantes</span>
                  <span className="dyd-value dyd-invites-count">{profile.invitationsLeft ?? 0}</span>
                </div>
                <button className="dyd-btn dyd-btn-ghost dyd-btn-sm" onClick={handleGenerateInvitation}>
                  <Plus size={14} /> Generar
                </button>
              </div>
            </div>

            {/* Bio */}
            <div className="dyd-panel dyd-info">
              <span className="dyd-label"><UserRound size={12} /> Bio</span>

              {isEditing ? (
                <>
                  <textarea
                    className="dyd-input"
                    rows={3}
                    maxLength={BIO_MAX_LENGTH}
                    value={formData.bio}
                    onChange={handleChange("bio")}
                    placeholder={DEFAULT_BIO}
                  />
                  <small className="dyd-hint">
                    Si la personalizas, se mostrará en tu tarjeta de la pestaña Networking.
                    {" "}{formData.bio.length}/{BIO_MAX_LENGTH}
                  </small>
                </>
              ) : (
                <span className="dyd-value">{profile.bio || DEFAULT_BIO}</span>
              )}
            </div>

            {/* Categorías de networking */}
            <div className="dyd-panel dyd-info">
              <span className="dyd-label"><Tags size={12} /> Categorías de networking</span>

              {isEditing ? (
                <>
                  <CategoryPicker
                    id="profileCategories"
                    value={formData.networkingCategories}
                    onChange={(categories) =>
                      setFormData({ ...formData, networkingCategories: categories })
                    }
                  />
                  <small className="dyd-hint">
                    Son las que te colocan en los filtros de la pestaña Networking.
                  </small>
                </>
              ) : profile.networkingCategories?.length > 0 ? (
                <div className="dyd-cat-chips">
                  {profile.networkingCategories.map((category) => (
                    <span key={category} className="dyd-chip">{category}</span>
                  ))}
                </div>
              ) : (
                <span className="dyd-hint">
                  Aún no has elegido ninguna categoría. Edita tu perfil para añadirlas.
                </span>
              )}
            </div>

            {/* LinkedIn */}
            <div className="dyd-panel dyd-info">
              <span className="dyd-label"><Linkedin size={12} /> LinkedIn</span>

              {isEditing ? (
                <>
                  <input
                    type="url"
                    className="dyd-input"
                    value={formData.linkedin || ""}
                    onChange={handleChange("linkedin")}
                    placeholder="https://www.linkedin.com/in/tu-perfil"
                  />
                  <small className="dyd-hint">
                    Añade tu perfil de LinkedIn para que otros miembros puedan conectar contigo profesionalmente.
                  </small>
                </>
              ) : profile.linkedin ? (
                <a
                  className="dyd-btn dyd-btn-ghost dyd-btn-sm"
                  href={profile.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ alignSelf: "flex-start" }}
                >
                  <Linkedin size={15} /> Visitar LinkedIn
                  <ExternalLink size={13} />
                </a>
              ) : (
                <span className="dyd-hint">No has añadido tu perfil de LinkedIn</span>
              )}
            </div>

            {isEditing && (
              <button className="dyd-btn dyd-btn-success" onClick={handleSaveChanges} style={{ alignSelf: "center" }}>
                <Save size={16} /> Guardar todos los cambios
              </button>
            )}

            <div className="dyd-note">
              <AlertCircle size={18} />
              <span>
                {formData.meetingAcceptance
                  ? "Actualmente estás incluido en los meetings periódicos. Para cambiarlo, contacta con el administrador."
                  : "Actualmente no estás incluido en los meetings periódicos. Para cambiarlo, contacta con el administrador."}
              </span>
            </div>
          </div>
        )}

        {/* ─── Datos profesionales ─── */}
        {activeTab === "profesional" && (
          <div className="dyd-pro-grid dyd-rise" style={{ "--i": 2 }}>

            {/* Ficha profesional */}
            <section>
              <div className="dyd-section-title">
                <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 9 }}>
                  <Building2 size={18} style={{ color: "var(--dyd-blue-light)" }} />
                  Ficha profesional
                </h3>
              </div>

              <div className="dyd-item-grid">
                {companies && companies.map((company, idx) => {
                  const isAccepted = company.accepted !== false;
                  return (
                    <ItemCard
                      key={company.name + idx}
                      accepted={isAccepted}
                      clickable={isEditing && isAccepted}
                      onClick={() => isEditing && isAccepted && editCompany(company)}
                    >
                      <span className="dyd-item-title">
                        {company.name}
                        {company.category && <em> ({company.category})</em>}
                      </span>
                      <span className="dyd-item-text">{company.description}</span>
                      {company.role && <span className="dyd-chip">{company.role}</span>}
                      {company.website && (
                        <a
                          href={getValidUrl(company.website)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dyd-item-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={12} /> {company.website}
                        </a>
                      )}
                    </ItemCard>
                  );
                })}

                {isEditing && (
                  <button className="dyd-add-card" onClick={() => editCompany(null)}>
                    <Plus size={20} /> Añadir empresa
                  </button>
                )}
              </div>

              <ProfesionalCard
                isOpen={showCompanyPopup}
                onClose={() => {
                  setShowCompanyPopup(false);
                  setSelectedCompany(null);
                }}
                company={selectedCompany}
              />
            </section>

            {/* Proyectos */}
            <section>
              <div className="dyd-section-title">
                <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 9 }}>
                  <Rocket size={18} style={{ color: "var(--dyd-blue-light)" }} />
                  Proyecto personal
                </h3>
              </div>

              <div className="dyd-item-grid">
                {projects && projects.map((project, idx) => {
                  const isAccepted = project.accepted !== false;
                  return (
                    <ItemCard
                      key={project.name + idx}
                      accepted={isAccepted}
                      clickable={isAccepted}
                      onClick={() => {
                        if (isEditing && isAccepted) editProject(project);
                        else if (!isEditing && isAccepted) window.location.href = `/project/${project._id}`;
                      }}
                    >
                      <span className="dyd-item-title">{project.name}</span>
                      <span className="dyd-item-text">{project.description}</span>
                      {project.link && (
                        <a
                          href={getValidUrl(project.link)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dyd-item-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={12} /> {project.link}
                        </a>
                      )}
                    </ItemCard>
                  );
                })}

                {isEditing && (
                  <button className="dyd-add-card" onClick={() => editProject(null)}>
                    <Plus size={20} /> Añadir proyecto
                  </button>
                )}
              </div>

              <ProjectCard
                isOpen={showProjectPopup}
                onClose={() => {
                  setShowProjectPopup(false);
                  setSelectedProject(null);
                }}
                project={selectedProject}
              />
            </section>
          </div>
        )}
      </div>

    </div>
  );
}
